import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Icon } from '../components/icons';
import {
  Badge, Button, Card, EmptyState, Field, Input, Spinner, Textarea,
} from '../components/ui';
import { Page, PageHeader, Section, Stack } from '../components/Page';
import type { InviteDto } from '../lib/types';

/**
 * Single-use links.
 *
 * The one thing this screen has to get right is that issued tokens are shown once. The
 * server stores only their hashes, so there is no "show again" to build even if a user
 * asks for one — the UI has to make that clear before they close the panel, not after.
 */
export function Invites() {
  const { formId = '' } = useParams();
  const queryClient = useQueryClient();

  const [issued, setIssued] = useState<{ id: string; url: string }[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [emails, setEmails] = useState('');
  const [count, setCount] = useState(5);
  const [expiryDays, setExpiryDays] = useState(14);
  const [byEmail, setByEmail] = useState(true);

  const { data, isLoading } = useQuery({
    queryKey: ['invites', formId],
    queryFn: () => api.invites.list(formId),
  });

  const issue = useMutation({
    mutationFn: () => {
      const expiresUtc = expiryDays > 0
        ? new Date(Date.now() + expiryDays * 86_400_000).toISOString()
        : undefined;

      const recipients = byEmail
        ? emails.split(/[\n,;]+/).map((e) => e.trim()).filter(Boolean).map((email) => ({ email }))
        : undefined;

      return api.invites.issue(formId, {
        recipients,
        count: byEmail ? undefined : count,
        expiresUtc,
        // The link's public URL has to be the one respondents can reach, which in
        // development is this dev server rather than the API port.
        baseUrl: window.location.origin,
      });
    },
    onSuccess: (result) => {
      setIssued(result.invites);
      setEmails('');
      void queryClient.invalidateQueries({ queryKey: ['invites', formId] });
    },
  });

  const revoke = useMutation({
    mutationFn: (ids: string[]) => api.invites.revoke(formId, ids),
    onSuccess: () => {
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['invites', formId] });
    },
  });

  const invites = data?.invites ?? [];
  const activeIds = invites.filter((i) => i.status === 'active').map((i) => i.id);

  return (
    <Page width="wide">
      <PageHeader
        icon="ticket"
        title="Single-use links"
        description="Each link opens the form once. Once a response is submitted through it,
                     the link stops working — including in a second tab that was already open."
      />

      <Stack>
      <Section title="Issue new links">

        <div className="mb-3 flex gap-4 text-sm">
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              checked={byEmail}
              onChange={() => setByEmail(true)}
            />
            One per email address
          </label>
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              checked={!byEmail}
              onChange={() => setByEmail(false)}
            />
            A number of anonymous links
          </label>
        </div>

        {byEmail ? (
          <Field
            label="Recipients"
            hint="One per line, or comma separated. The address is a label for your own
                  reporting — nothing is emailed from here yet."
          >
            <Textarea
              rows={4}
              value={emails}
              placeholder={'ana@example.com\nbo@example.com'}
              onChange={(e) => setEmails(e.target.value)}
            />
          </Field>
        ) : (
          <Field label="How many">
            <Input
              type="number"
              min={1}
              max={500}
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
            />
          </Field>
        )}

        <Field
          label="Expires after (days)"
          hint="0 for no expiry. An expiring link limits the damage if one is forwarded."
        >
          <Input
            type="number"
            min={0}
            max={365}
            value={expiryDays}
            onChange={(e) => setExpiryDays(Number(e.target.value))}
          />
        </Field>

        <div className="mt-3 flex items-center gap-3">
          <Button
            onClick={() => issue.mutate()}
            disabled={issue.isPending || (byEmail && emails.trim() === '')}
          >
            {issue.isPending ? 'Issuing…' : 'Issue links'}
          </Button>
          {issue.error && (
            <span className="text-sm text-[var(--ff-danger)]">
              {(issue.error as Error).message}
            </span>
          )}
        </div>
      </Section>

      {issued && (
        <Card className="mb-6 border-[var(--ff-primary)] p-4">
          <div className="mb-2 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold">
                {issued.length} link{issued.length === 1 ? '' : 's'} issued
              </h2>
              <p className="mt-1 text-xs text-[var(--ff-danger,#dc2626)]">
                Copy these now. The server stores only a hash of each token, so they cannot
                be shown again — closing this panel loses them for good.
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  void navigator.clipboard.writeText(issued.map((i) => i.url).join('\n'));
                }}
              >
                <Icon name="copy" size={14} /> Copy all
              </Button>
              <Button variant="secondary" onClick={() => setIssued(null)}>Done</Button>
            </div>
          </div>

          <textarea
            readOnly
            rows={Math.min(10, issued.length + 1)}
            value={issued.map((i) => i.url).join('\n')}
            onFocus={(e) => e.currentTarget.select()}
            className="w-full rounded border border-[var(--ff-border)]
                       bg-[var(--ff-surface-sunken)] p-2 font-mono text-xs"
          />
        </Card>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">
          Issued links
          {data?.summary && (
            <span className="ml-2 font-normal text-[var(--ff-text-muted)]">
              {Object.entries(data.summary)
                .map(([status, n]) => `${n} ${status}`)
                .join(' · ')}
            </span>
          )}
        </h2>

        {selected.size > 0 && (
          <Button
            variant="danger"
            onClick={() => revoke.mutate([...selected])}
            disabled={revoke.isPending}
          >
            Revoke {selected.size} selected
          </Button>
        )}
      </div>

      {isLoading && <Spinner label="Loading links" />}

      {!isLoading && invites.length === 0 && (
        <EmptyState
          title="No links yet"
          description="Issue some above to send this form to specific people."
        />
      )}

      {invites.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-[var(--ff-border)]">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-[var(--ff-surface-sunken)] text-left">
              <tr>
                <th className="w-10 px-3 py-2">
                  <input
                    type="checkbox"
                    aria-label="Select all active links"
                    className="h-4 w-4 accent-[var(--ff-primary)]"
                    checked={activeIds.length > 0 && selected.size === activeIds.length}
                    onChange={(e) =>
                      setSelected(e.target.checked ? new Set(activeIds) : new Set())}
                  />
                </th>
                <th className="px-3 py-2 font-medium">Link</th>
                <th className="px-3 py-2 font-medium">Recipient</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Opened</th>
                <th className="px-3 py-2 font-medium">Used</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((invite) => (
                <InviteRow
                  key={invite.id}
                  invite={invite}
                  selected={selected.has(invite.id)}
                  onToggle={(on) => {
                    const next = new Set(selected);
                    if (on) next.add(invite.id); else next.delete(invite.id);
                    setSelected(next);
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      </Stack>
    </Page>
  );
}

function InviteRow({
  invite, selected, onToggle,
}: { invite: InviteDto; selected: boolean; onToggle: (on: boolean) => void }) {
  const tone = {
    active: 'primary',
    consumed: 'success',
    revoked: 'neutral',
    expired: 'neutral',
  }[invite.status] as 'primary' | 'success' | 'neutral';

  return (
    <tr className="border-t border-[var(--ff-border)]">
      <td className="px-3 py-2">
        <input
          type="checkbox"
          aria-label={`Select link ${invite.tokenPrefix}`}
          className="h-4 w-4 accent-[var(--ff-primary)]"
          checked={selected}
          disabled={invite.status !== 'active'}
          onChange={(e) => onToggle(e.target.checked)}
        />
      </td>
      <td className="px-3 py-2 font-mono text-xs">
        {/* The prefix identifies a link in a support conversation and is useless on its own. */}
        {invite.tokenPrefix}…
      </td>
      <td className="px-3 py-2">
        {invite.recipientEmail ?? (
          <span className="text-[var(--ff-text-muted)]">anonymous</span>
        )}
      </td>
      <td className="px-3 py-2">
        <Badge tone={tone}>{invite.status}</Badge>
        {invite.maxUses > 1 && (
          <span className="ml-2 text-xs text-[var(--ff-text-muted)]">
            {invite.useCount}/{invite.maxUses}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-[var(--ff-text-muted)]">
        {invite.firstOpenedUtc ? new Date(invite.firstOpenedUtc).toLocaleString() : '—'}
      </td>
      <td className="px-3 py-2 text-[var(--ff-text-muted)]">
        {invite.consumedUtc ? new Date(invite.consumedUtc).toLocaleString() : '—'}
      </td>
    </tr>
  );
}
